import Pusher from "pusher-js";
import { debugLog } from "../utils";

Pusher.logToConsole = process.env.PARCEL_DEBUG_MODE === "true";

class ChannelServiceInternal {
  constructor() {
    this.types = {}; // { [type]: { name, channel, status, pendingName, debounceId } }
    this.pusher = new Pusher(process.env.PARCEL_PUSHER_KEY, {
      cluster: process.env.PARCEL_PUSHER_CLUSTER || "us2",
    });

    this.pusher.connection.bind("state_change", s => debugLog("pusher state:", s));
    this.pusher.connection.bind("error", e => console.warn("pusher error:", e));

    this.connect(); // meta tag bootstrap
    window.addEventListener("beforeunload", () => this.disconnect());

    // Optional: close modals before *any* Turbo Stream render (covers non-Pusher streams too)
    document.addEventListener("turbo:before-stream-render", () => this._closeAllModalsNow());
  }

  connect(channel = null) {
    const ch =
      (typeof channel === "string" && channel.trim()) ||
      this._readChannelFromDOM();
    if (!ch) return;
    this.subscribeToChannel("stream", ch);
  }

  disconnect() {
    Object.keys(this.types).forEach(type => this._forceUnsubscribe(type));
    this.pusher.disconnect();
  }

  _readChannelFromDOM() {
    const el = document.querySelector('meta[name="pusher-channel"]');
    return el?.content?.trim() || null;
  }

  // --- Modal close helper (global event + hard fallback) ---
  _closeAllModalsNow() {
    // Let modal controllers clean up (move focus, set inert/aria-hidden)
    document.dispatchEvent(new CustomEvent("modal:close-all", { bubbles: true }));

    // Fallback: slam any visible modal nodes shut if they remain
/*     document
      .querySelectorAll('[data-controller~="modal"] [data-modal-target="modal"]:not(.hidden)')
      .forEach(el => {
        try {
          el.setAttribute("inert", "");
          el.setAttribute("aria-hidden", "true");
          el.classList.add("hidden");
        } catch (_) {}
      }); */
  }

  subscribeToChannel(type, identifier) {
    const channelName = (identifier || "").trim();
    if (!channelName) return;

    // simple debounce in case Stimulus connect fires multiple times rapidly
    clearTimeout(this.types[type]?.debounceId);
    this.types[type] = this.types[type] || { status: "idle" };
    this.types[type].debounceId = setTimeout(() => this._subscribeCore(type, channelName), 0);
  }

  _subscribeCore(type, channelName) {
    const state = this.types[type] || { status: "idle" };

    // If already subscribed/subscribing to exactly this name → no-op
    if ((state.status === "subscribed" || state.status === "subscribing") && state.name === channelName) {
      debugLog("ChannelService: already on channel", { type, channelName, status: state.status });
      return;
    }

    // If a subscribe is in-flight to a different name, remember the desired one and switch after
    if (state.status === "subscribing") {
      state.pendingName = channelName;
      debugLog("ChannelService: queue switch after subscribe", { type, from: state.name, to: channelName });
      return;
    }

    // If subscribed to a different channel, cleanly unsubscribe first
    if (state.status === "subscribed" && state.name && state.name !== channelName) {
      this._safeUnsubscribe(type);
    }

    // Begin subscribe
    debugLog("ChannelService: subscribing", { type, channelName });
    const channel = this.pusher.subscribe(channelName);

    // Update local state *before* events arrive
    this.types[type] = { name: channelName, channel, status: "subscribing", pendingName: null };

    // Pusher lifecycle binds
    channel.bind("pusher:subscription_succeeded", () => {
      const curr = this.types[type];
      if (!curr || curr.name !== channelName) return; // stale
      curr.status = "subscribed";
      debugLog("ChannelService: subscription_succeeded", { type, channelName });

      // If a newer request arrived while subscribing, switch now
      if (curr.pendingName && curr.pendingName !== curr.name) {
        const next = curr.pendingName;
        curr.pendingName = null;
        this._safeUnsubscribe(type, () => this._subscribeCore(type, next));
      }
    });

    channel.bind("pusher:subscription_error", (err) => {
      const curr = this.types[type];
      if (curr && curr.name === channelName) curr.status = "idle";
      console.warn("ChannelService: subscription_error", { type, channelName, err });
    });

    // --- App events ---

    // Direct Turbo Stream HTML payloads
    channel.bind("html", (data) => {
      this._closeAllModalsNow();              // 👈 close modals first
      Turbo.renderStreamMessage(data);
    });

    // Simple redirect payloads — allow same-origin and any absolute http(s):// URL.
    // Normalise bare domains like "www.google.com" → "https://www.google.com".
    channel.bind("redirect", (data) => {
      if (data?.url) {
        try {
          const raw = String(data.url).trim();
          if (!raw) return;

          const normalized = !/^https?:\/\//i.test(raw) && !raw.startsWith("/")
            ? `https://${raw}`
            : raw;

          const u = new URL(normalized, window.location.origin);
          if (u.protocol !== "http:" && u.protocol !== "https:") return;

          window.location.assign(u.toString());
        } catch {
          debugLog("Invalid redirect URL payload:", data?.url);
        }
      }
    });

    // Direct deposit events — dispatched as DOM events for DirectDepositController
    channel.bind("deposit_confirmed", (data) => {
      debugLog("deposit_confirmed event received:", data);
      window.dispatchEvent(new CustomEvent("dd:deposit-confirmed", { detail: data }));
    });

    channel.bind("deposit_insufficient", (data) => {
      debugLog("deposit_insufficient event received:", data);
      window.dispatchEvent(new CustomEvent("dd:deposit-insufficient", { detail: data }));
    });

    channel.bind("deposit_manual_check", (data) => {
      debugLog("deposit_manual_check event received:", data);
      window.dispatchEvent(new CustomEvent("dd:deposit-manual-check", { detail: data }));
    });
    // Onramp pending — fired by the WaaS backend on the first Meld
    // pending/transferring webhook, BEFORE funds settle and well before
    // consolidation. AddFundsWalletController uses this to lock the checkout
    // into a spinner the instant an onramp is in flight, so the shopper can't
    // pick another deposit option and race the consolidation swap.
    channel.bind("onramp_pending", (data) => {
      debugLog("onramp_pending event received:", data);
      window.dispatchEvent(new CustomEvent("waas:onramp_pending", { detail: data }));
    });

    // Onramp received — fired by the WaaS backend the instant a Meld/card
    // webhook arrives, BEFORE consolidation + ledger + waas_funded. The buy
    // app's AddFundsWalletController uses this to flip the spinner from
    // "Fund your wallet in the other tab" → "Please wait, finalizing payment"
    // so the shopper isn't staring at a stale UI during the ~10–20 s wait.
    channel.bind("onramp_received", (data) => {
      debugLog("onramp_received event received:", data);
      window.dispatchEvent(new CustomEvent("waas:onramp_received", { detail: data }));
    });

    // WaaS funded — fired by the WaaS backend after a Meld/card deposit settles.
    // AddFundsWalletController listens for this to auto-redirect to Pay Now.
    channel.bind("waas_funded", (data) => {
      debugLog("waas_funded event received:", data);
      window.dispatchEvent(new CustomEvent("waas:funded", { detail: data }));
    });

    // Onramp failed — fired by the WaaS backend on Meld's TRANSACTION_CRYPTO_FAILED
    // webhook (card declined, KYC rejected, provider cancelled). AddFundsWalletController
    // uses it to replace the "confirming your payment" spinner with a retry / choose-
    // another-method view instead of waiting out the timeout (COI-833).
    channel.bind("onramp_failed", (data) => {
      debugLog("onramp_failed event received:", data);
      window.dispatchEvent(new CustomEvent("waas:onramp_failed", { detail: data }));
    });

    // Fetch-then-render Turbo Stream HTML
    channel.bind("stream", async (payload) => {
      // Accept either object or JSON string payloads
      let data = payload;
      if (typeof data === "string") {
        try { data = JSON.parse(data); } catch { debugLog("Stream payload not JSON:", payload); return; }
      }

      debugLog("Stream Event:", data);
      const url = data?.url;
      if (!url) return;

      const u = new URL(url, window.location.origin);
      if (u.origin !== window.location.origin) return;

      try {
        debugLog("Trying to access url:", u.toString());
        const res = await fetch(u.toString(), {
          method: "GET",
          credentials: "same-origin",
          headers: { "X-Requested-With": "XMLHttpRequest" },
        });
        const json = await res.json();
        if (json?.type === "html" && json.data) {
          this._closeAllModalsNow();          // 👈 close modals first
          Turbo.renderStreamMessage(json.data);
        }
      } catch (err) {
        debugLog("Failed to fetch Turbo Stream JSON:", err);
      }
    });
  }

  _safeUnsubscribe(type, then) {
    const state = this.types[type];
    if (!state?.channel || state.status === "unsubscribing") {
      if (then) then();
      return;
    }
    state.status = "unsubscribing";
    debugLog("ChannelService: unsubscribing", { type, channelName: state.name });

    // Pusher has no explicit callback for client-initiated unsubscribe success; do local cleanup immediately.
    state.channel.unbind_all();
    this.pusher.unsubscribe(state.name);
    // Reset state
    this.types[type] = { status: "idle" };
    if (then) then();
  }

  _forceUnsubscribe(type) {
    const state = this.types[type];
    if (!state?.channel) return;
    try {
      state.channel.unbind_all();
      this.pusher.unsubscribe(state.name);
    } finally {
      this.types[type] = { status: "idle" };
    }
  }
}

// Hard singleton export
if (!window.__ChannelService) window.__ChannelService = new ChannelServiceInternal();
export default window.__ChannelService;
